import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { VoiceMicButton } from '../components/VoiceMicButton';
import { RealTimeMetricsCard, MetricData } from '../components/RealTimeMetricsCard';
import { Waveform } from '../components/Waveform';
import { UpgradePromptModal } from '../components/upgrade/UpgradePromptModal';
import { LiveConversationPanel } from '../components/LiveConversationPanel';
import { UnfinishedPracticeSessions } from '../components/practice/UnfinishedPracticeSessions';
import { clampOrNull } from '../utils/scoreDisplay';
import { planLimitNotice, usageAuthorityUnavailableMessage, type PlanLimitNotice } from '../utils/errorHelpers';
import { beginRecordedAttempt, type RecordedAttemptPlan } from '../utils/recordedAttempt';
import { attemptErrorView, ATTEMPT_ERROR_COPY, type AttemptErrorView } from '../utils/attemptErrors';
import { npcAttemptOverall, sessionAttemptOverall } from '../utils/npcAttemptResult';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import { useCreateCoachingSessionMutation, useFinalizeCoachingSessionMutation } from '../store/api/coachingApi';
import { useSpokenAttemptFinalizer } from '../realtime/spokenAttemptFinalize';
import { SpokenAttemptOutcome } from '../components/SpokenAttemptOutcome';
import {
  useCreatePracticeSessionMutation,
  useFinalizePracticeSessionMutation,
  FinalizeSessionResponse,
  trainingSessionRequest,
  AI_COACH_SESSION_TYPE,
  type TrainingOutcome,
} from '../store/api/practiceApi';
import { levelConversationUrl } from '../utils/flowLinks';
import { AssignmentContextBanner, AssignmentOutcomeCard } from '../components/flows/FlowOutcomes';
import {
  useGetNpcPersonasQuery,
  useGetNpcLevelsQuery,
  useCreateNpcSessionMutation,
  useFinalizeNpcSessionMutation,
  PersonaWithLevelSummary,
  LevelWithProgress,
  DifficultyTier,
  FinalizeNpcSessionResponse,
} from '../store/api/npcApi';
import { asArray } from '../store/api/npcResponses';
import { NpcLevelResultCard } from '../components/practice/NpcLevelResultCard';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { useUsageAllowance } from '../hooks/useUsageAllowance';
import { allowanceText, isKnownExhausted } from '../utils/billingStatusView';
import { isRecordingRefusalNotice, useVoicePractice } from '../hooks/useVoicePractice';
import { RecordingRefusalNotice } from '../components/practice/RecordingRefusalNotice';
import { AddCreditsAction } from '../components/billing/AddCreditsAction';
import {
  useListContentSetsQuery,
  useListMyAssignmentsQuery,
  type SessionConfig,
  // type ContentSet, // Commented out - not currently used
} from '../store/api/trainingApi';
import {
  useGetBlueprintsQuery,
  useGetBlueprintSummaryQuery,
  BlueprintSummary,
  FLOWER_LABELS as BP_FLOWER_LABELS,
  BOUQUET_LABELS,
  STAGE_POSITION_LABELS,
  STAGE_POSITION_ICONS,
  EVENT_TYPE_LABELS,
} from '../store/api/blueprintsApi';
import {
  useGetMyAuthorityQuery,
  type AuthorityLevelKey,
} from '../store/api/gamificationApi';

type SessionState = 'idle' | 'recording' | 'processing' | 'completed';
type PracticeMode = 'free' | 'ai_coach' | 'npc' | 'rag';
type PracticeType = 'classic' | 'live';
type PracticeTab = 'practice' | 'feedback';
/** Guided entry phases for live mode */
type EntryPhase = 'guide' | 'precall' | 'session';

/**
 * The training config MyAssignmentsPage passes in navigation state (from
 * POST /training/assignments/:id/start). Only the assignment id is sent back;
 * the server derives org and module from the assignment row.
 */
function readTrainingConfig(state: unknown): SessionConfig | null {
  const config = (state as { trainingConfig?: Partial<SessionConfig> } | null)?.trainingConfig;
  return config && typeof config.trainingAssignmentId === 'string' && config.trainingAssignmentId
    ? (config as SessionConfig)
    : null;
}

/** Which finalize endpoint a recorded session goes to once its transcript is ready. */
type FinalizeTarget = { kind: 'npc' | 'ai_coach' | 'training'; sessionId: string };

export function PracticePage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Training assignment context (My Training → Start/Continue/Retry Training).
  const trainingConfig = useMemo(() => readTrainingConfig(location.state), [location.state]);
  const { data: myAssignments } = useListMyAssignmentsQuery(undefined, { skip: !trainingConfig });
  const practisedAssignment = trainingConfig
    ? myAssignments?.find((a) => a.id === trainingConfig.trainingAssignmentId)
    : undefined;
  /** What the finalized session did to the assignment; null = could not be confirmed. */
  const [trainingOutcome, setTrainingOutcome] = useState<TrainingOutcome | null | undefined>(undefined);
  const [startError, setStartError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PracticeTab>(() => {
    return searchParams.get('tab') === 'feedback' ? 'feedback' : 'practice';
  });
  const [sessionState, setSessionState] = useState<SessionState>('idle');
  const [practiceMode, setPracticeMode] = useState<PracticeMode>(() => {
    const mode = searchParams.get('mode');
    if (mode === 'npc' || mode === 'ai_coach' || mode === 'rag') return mode;
    return 'free';
  });
  // What the user CHOSE. What is shown is `practiceType` below, which also
  // requires the server's realtime capability, so a choice can never outlive it.
  // Record & Review is the default; no query parameter selects Live Conversation.
  const [requestedPracticeType, setPracticeType] = useState<PracticeType>('classic');
  const [hasSession, setHasSession] = useState(false);
  const [finalSummary, setFinalSummary] = useState<string | null>(null);
  const [finalResult, setFinalResult] = useState<FinalizeSessionResponse | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string | null>(null);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);

  // Guided entry phase for live mode
  const [entryPhase, setEntryPhase] = useState<EntryPhase>(() => {
    // Skip guided entry if persona pre-selected via query param or mode explicitly set
    if (searchParams.get('persona') || searchParams.get('mode')) return 'session';
    return 'guide';
  });

  // NPC Practice state
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(() => {
    return searchParams.get('persona');
  });
  const [selectedLevelId, setSelectedLevelId] = useState<string | null>(null);
  const [npcDomainFilter, setNpcDomainFilter] = useState<string>('');
  // `?scenario=` pre-filters NPC personas (Learn → Practice lesson links).
  const [npcScenarioFilter, setNpcScenarioFilter] = useState<string>(
    () => searchParams.get('scenario') ?? ''
  );
  const [npcTierFilter, setNpcTierFilter] = useState<DifficultyTier | ''>('');
  const [npcFinalResult, setNpcFinalResult] = useState<FinalizeNpcSessionResponse | null>(null);

  // RAG/Content Set Practice state
  const [_selectedContentSetId, setSelectedContentSetId] = useState<string | null>(null);

  // Central capability checks (authorization)
  const { hasCapability, bootCompleted } = useCentralConsumer();

  // Monthly AI voice coaching allowance — display only, never gating; unknown
  // renders as unavailable, not 0 (the server admits or refuses each use).
  const {
    coaching: coachingAllowance,
    refetch: refetchAllowance,
  } = useUsageAllowance();
  const coachingAllowanceText = allowanceText(coachingAllowance);

  // Upgrade prompt UI state (local state, not authorization)
  const [upgradePrompt, setUpgradePrompt] = useState<{
    isOpen: boolean;
    reason: string;
    message: string;
    action: string;
    planLimit?: PlanLimitNotice;
  }>({ isOpen: false, reason: '', message: '', action: '' });
  const hideUpgradePrompt = () =>
    setUpgradePrompt({ isOpen: false, reason: '', message: '', action: '' });

  /**
   * A start refused by a usage or safety limit (402 USAGE_BLOCKED, 429
   * CONVERSATION_SAFETY_LIMIT): the limit's own message (the server's, which
   * carries the number, else the shared copy) — never a generic "could not be
   * started", and never "try again" for a limit that frees up with time.
   */
  const limitRefusalMessage = (error: unknown): string | null => {
    const view = attemptErrorView(error, '');
    if (view.code !== 'USAGE_BLOCKED' && view.code !== 'CONVERSATION_SAFETY_LIMIT') return null;
    const serverMessage = (error as { data?: { message?: unknown } })?.data?.message;
    return typeof serverMessage === 'string' && serverMessage.trim() ? serverMessage : view.message;
  };

  /**
   * A session start the server refused for the plan's limit (403 PLAN_LIMIT):
   * show its message as sent and start nothing — no recording, no session.
   * Returns false for any other failure.
   */
  const refusedForPlanLimit = (error: unknown, action: string): boolean => {
    const limit = planLimitNotice(error);
    if (!limit) return false;
    setUpgradePrompt({ isOpen: true, reason: 'plan_limit', message: limit.message, action, planLimit: limit });
    finalizeTargetRef.current = null;
    setCurrentSessionId(null);
    setHasSession(false);
    setSessionState('idle');
    return true;
  };

  /**
   * Any other refused start: say why and leave nothing started — no session,
   * no recording, nothing to upload.
   */
  const refuseStart = (message: string) => {
    finalizeTargetRef.current = null;
    setCurrentSessionId(null);
    setHasSession(false);
    setSessionState('idle');
    setStartError(message);
  };

  // Only the Central boot gates this page; the billing read never blocks it.
  const isPlanLoading = !bootCompleted;

  // ── LIVE CONVERSATION IS GATED BY THE SERVER, AT RUNTIME ───────────────────
  //
  // Same source as CoachingPage: the server reports the conjunction of the
  // conversation engine and realtime voice as one boolean. Unloaded or
  // unreadable capabilities mean "not available", so Practice is Record &
  // Review only — no button that fails when pressed.
  const { data: capabilities, isSuccess: capabilitiesLoaded } = useGetCapabilitiesQuery();
  const realtimeVoiceAvailable = capabilitiesLoaded
    ? capabilities.conversation.realtimeVoice === true
    : false;
  const practiceType: PracticeType = realtimeVoiceAvailable ? requestedPracticeType : 'classic';
  // A conversation-based level attempt needs the canonical engine (the legacy
  // runtime binds no level). Unloaded or unreadable: not offered.
  const conversationEngineAvailable = capabilitiesLoaded && capabilities.conversation.engine === true;
  // Private Knowledge (RAG) needs real semantic retrieval on the server. Until
  // it reports `rag: true`, the mode is offered as unavailable — never as a
  // practice that "asks questions based on your uploaded materials".
  const ragAvailable = capabilitiesLoaded && capabilities.rag === true;
  const accessToken = useSelector((st: RootState) => st.centralAuth?.accessToken);
  const [createCoachingSession] = useCreateCoachingSessionMutation();
  const [liveStartError, setLiveStartError] = useState<string | null>(null);
  // The live start was refused for credits: the notice offers Add Credits / "ask your owner".
  const [liveStartErrorIsCredits, setLiveStartErrorIsCredits] = useState(false);
  // A Live Conversation is scored when it ENDS (End pressed, or the server
  // completing it): the panel reports the end once per attempt and the
  // canonical finalize runs once for it. Its result is shown here.
  const [finalizeCoachingSession] = useFinalizeCoachingSessionMutation();
  const { finalizer: liveFinalizer, state: liveOutcome, clear: clearLiveOutcome } = useSpokenAttemptFinalizer(
    (id) => finalizeCoachingSession(id).unwrap()
  );
  const [liveRetrying, setLiveRetrying] = useState(false);

  // Blueprints for structured practice
  const { data: blueprints = [] } = useGetBlueprintsQuery();
  const { data: blueprintSummary } = useGetBlueprintSummaryQuery(
    selectedBlueprintId || '',
    { skip: !selectedBlueprintId }
  );

  // Content Sets for RAG-enhanced practice
  const { data: userContentSets = [] } = useListContentSetsQuery(
    { ownerType: 'user', ownerId: 'me' },
    { skip: practiceMode !== 'rag' }
  );

  // Authority level for gamification hints
  const { data: authority } = useGetMyAuthorityQuery();

  // NPC Personas and Levels — also fetched for guided entry in live mode
  const shouldFetchPersonas = practiceMode === 'npc' || (practiceType === 'live' && entryPhase === 'guide');
  const { data: npcPersonasData, isLoading: isLoadingPersonas } = useGetNpcPersonasQuery(
    {
      domainKey: npcDomainFilter || undefined,
      difficultyTier: npcTierFilter || undefined,
      ...(npcScenarioFilter ? { scenarioKey: npcScenarioFilter } : {}),
    },
    { skip: !shouldFetchPersonas }
  );
  // Also fetch ALL personas (unfiltered) for the guided entry to derive topics from
  const { data: allPersonasData } = useGetNpcPersonasQuery(
    { domainKey: npcDomainFilter || undefined },
    { skip: entryPhase !== 'guide' || practiceType !== 'live' }
  );
  const {
    data: npcLevelsData,
    isLoading: isLoadingLevels,
    isError: isLevelsError,
  } = useGetNpcLevelsQuery(selectedPersonaId || '', { skip: !selectedPersonaId });
  // API data is only ever used as an array here: a non-array (an envelope, an
  // error body) is "levels unavailable", never something `.find` is called on.
  // (`npcLevels.find` on the `{ persona, levels }` object blanked this page.)
  const npcPersonas = asArray<PersonaWithLevelSummary>(npcPersonasData);
  const allPersonas = asArray<PersonaWithLevelSummary>(allPersonasData);
  const npcLevels = asArray<LevelWithProgress>(npcLevelsData);
  const npcLevelsUnavailable =
    !!selectedPersonaId &&
    !isLoadingLevels &&
    (isLevelsError || (npcLevelsData !== undefined && !Array.isArray(npcLevelsData)));
  const selectedPersona = npcPersonas.find((p) => p.id === selectedPersonaId);
  const [createNpcSession] = useCreateNpcSessionMutation();
  const [finalizeNpcSession] = useFinalizeNpcSessionMutation();

  const canUseAiCoach = hasCapability('ai', 'coach');

  // RTK Query mutations
  const [createSession] = useCreatePracticeSessionMutation();
  const [finalizeSession, { isLoading: isFinalizingSession }] = useFinalizePracticeSessionMutation();

  // ── FINALIZE WHEN THE TRANSCRIPT ACTUALLY EXISTS ──────────────────────────
  //
  // Finalize used to run right after `await stopRecording()`, reading
  // `voiceTranscript` from the render that started the recording — always null
  // there, so NPC, AI-coach and training sessions were never finalized. The
  // session to finalize is held in a ref and finalized from onTranscriptReady,
  // which receives the transcript itself.
  const finalizeTargetRef = useRef<FinalizeTarget | null>(null);
  const sessionDurationRef = useRef(0);

  /**
   * A refused finalize, told to the user from the one code→copy map
   * (utils/attemptErrors). Nothing is shown as a result: a retryable refusal
   * offers "Try again" with the same transcript; an already-finalized attempt
   * shows the outcome the server stored (error.details.outcome).
   */
  const [finalizeError, setFinalizeError] = useState<AttemptErrorView | null>(null);
  const [finalizeRetrying, setFinalizeRetrying] = useState(false);
  const lastFinalizeRef = useRef<{ target: FinalizeTarget; transcript: string; durationSeconds: number } | null>(null);

  const runFinalize = async (target: FinalizeTarget, transcript: string, durationSeconds: number) => {
    lastFinalizeRef.current = { target, transcript, durationSeconds };
    setFinalizeError(null);
    const data = { transcript, durationSeconds };
    const refused = (error: unknown) => {
      const view = attemptErrorView(error, 'Your attempt could not be finalized, so nothing was scored or recorded. Try again.');
      // An unknown failure is worth one more try; a known code says whether.
      setFinalizeError(view.code ? view : { ...view, retryable: true });
      if (view.storedOutcome?.feedback?.summary) {
        setFinalSummary(view.storedOutcome.feedback.summary);
      }
    };
    if (target.kind === 'npc') {
      try {
        const result = await finalizeNpcSession({ id: target.sessionId, data }).unwrap();
        setNpcFinalResult(result);
        setFinalResult(null);
        setFinalSummary((current) => current ?? result.feedback.summary);
      } catch (error) {
        refused(error);
      }
      return;
    }
    try {
      const result = await finalizeSession({ id: target.sessionId, data }).unwrap();
      setFinalResult(result);
      setNpcFinalResult(null);
      setFinalSummary((current) => current ?? result.feedback.summary);
      if (target.kind === 'training') setTrainingOutcome(result.training ?? null);
    } catch (error) {
      refused(error);
      if (target.kind === 'training') setTrainingOutcome(null);
    }
  };

  const finalizeAfterTranscript = async (transcript: string) => {
    const target = finalizeTargetRef.current;
    finalizeTargetRef.current = null; // once per recording
    if (!target) return;
    // Nothing heard: not an attempt, and said so (it used to end silently).
    if (!/[\p{L}\p{N}]/u.test(transcript ?? '')) {
      setFinalizeError({ code: 'NO_SPEECH_DETECTED', ...ATTEMPT_ERROR_COPY.NO_SPEECH_DETECTED });
      return;
    }
    await runFinalize(target, transcript, sessionDurationRef.current);
  };

  const retryFinalize = async () => {
    const last = lastFinalizeRef.current;
    if (!last || finalizeRetrying) return;
    setFinalizeRetrying(true);
    try {
      await runFinalize(last.target, last.transcript, last.durationSeconds);
    } finally {
      setFinalizeRetrying(false);
    }
  };

  const recordingNeedsSession =
    !!trainingConfig || (practiceMode === 'npc' && !!selectedPersonaId) || practiceMode === 'ai_coach';

  // Voice practice hook for real audio recording and transcription (Classic mode)
  const {
    status: voiceStatus,
    transcript: voiceTranscript,
    feedbackText: voiceFeedback,
    feedbackSource: voiceFeedbackSource,
    scores: voiceScores,
    evidence: voiceEvidence,
    analysis: voiceAnalysis,
    error: voiceError,
    errorCode: voiceErrorCode,
    recordingDuration,
    startRecording,
    stopRecording,
    reset: resetVoice,
    isSupported: isVoiceSupported,
  } = useVoicePractice({
    sessionId: practiceMode === 'ai_coach' ? currentSessionId : null,
    analysisSessionId: practiceMode === 'ai_coach' ? null : currentSessionId,
    // Training, NPC and AI-coach recordings belong to a session: without one
    // (the start was refused) nothing is uploaded or analyzed.
    requireSession: recordingNeedsSession,
    onTranscriptReady: (transcript) => {
      void finalizeAfterTranscript(transcript);
    },
    onScoresReady: (scores) => {
      console.log('Scores ready:', scores);
    },
    onError: (error) => {
      console.error('Voice practice error:', error);
    },
  });

  /**
   * Create the canonical spoken attempt for Live Conversation.
   *
   * Called by LiveConversationPanel when the user presses Start, never before.
   * The request names only WHO (a persona key) and HOW (realtime audio,
   * Development). The server resolves the persona through its persona authority
   * (NPC ladder personas included) and binds prompt and content itself; nothing
   * client-authored is sent.
   */
  const beginLiveAttempt = async (): Promise<string | null> => {
    setLiveStartError(null);
    setLiveStartErrorIsCredits(false);
    // A new attempt: the previous one's result is put away (it stays in history).
    clearLiveOutcome();
    try {
      const session = await createCoachingSession({
        persona: practiceMode === 'npc' && selectedPersona ? selectedPersona.key : 'default',
        modality: 'realtime_audio',
        mode: 'development',
      }).unwrap();
      return session.id;
    } catch (error) {
      const code = (error as { data?: { error?: { code?: string } } })?.data?.error?.code;
      const status = (error as { status?: number })?.status;
      if (code === 'INSUFFICIENT_CREDITS' || status === 402) {
        setLiveStartError('You do not have enough credits to start this session. Open Manage billing (in Settings → Billing) to review your plan and credits.');
        setLiveStartErrorIsCredits(true);
      } else if (code && ATTEMPT_ERROR_COPY[code]) {
        // CENTRAL_UNAVAILABLE, USAGE_AUTHORITY_UNAVAILABLE, a busy session…:
        // worded once, in utils/attemptErrors.
        setLiveStartError(ATTEMPT_ERROR_COPY[code].message);
      } else if (status === 503) {
        setLiveStartError('Sessions are temporarily unavailable. Please try again shortly.');
      } else {
        setLiveStartError('Failed to start the live conversation. Please try again.');
      }
      return null;
    }
  };

  // Sync voice hook status with session state
  useEffect(() => {
    if (voiceStatus === 'processing') {
      setSessionState('processing');
    } else if (voiceStatus === 'complete') {
      setSessionState('completed');
      if (voiceFeedback) {
        setFinalSummary(voiceFeedback);
      }
    } else if (voiceStatus === 'error') {
      setSessionState('idle');
    }
  }, [voiceStatus, voiceFeedback]);

  // Update session duration from voice hook when recording
  useEffect(() => {
    if (voiceStatus === 'recording') {
      setSessionDuration(recordingDuration);
      sessionDurationRef.current = recordingDuration;
    }
  }, [voiceStatus, recordingDuration]);

  const isRecording = sessionState === 'recording' || voiceStatus === 'recording';
  const isProcessing = sessionState === 'processing' || voiceStatus === 'processing';

  // Convert voice scores to metrics array for RealTimeMetricsCard
  const metrics: MetricData[] = useMemo(() => {
    // Cast to any to handle legacy score properties that may exist at runtime.
    // R0: an absent dimension stays null ("not measured"). It is NOT coerced to 0,
    // and one dimension is never substituted for another (`pace || structure` and
    // `confidence || delivery` were both doing exactly that).
    const scores = (voiceScores || {}) as any;
    return [
      { label: 'Clarity', value: clampOrNull(scores.clarity), target: 85 },
      { label: 'Pace', value: clampOrNull(scores.pace), target: 80 },
      {
        label: 'Confidence',
        value: clampOrNull(scores.confidence),
        target: 75,
        unavailableNote: 'Vocal delivery analysis is not available yet',
      },
      {
        label: 'Energy',
        value: clampOrNull(scores.energy),
        target: 80,
        unavailableNote: 'Vocal delivery analysis is not available yet',
      },
    ];
  }, [voiceScores]);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleToggleRecording = async () => {
    if (isRecording) {
      // Stop recording. The voice hook transcribes, then onTranscriptReady
      // finalizes the session held in finalizeTargetRef.
      await stopRecording();
    } else {
      // Start recording
      setSessionState('recording');
      setHasSession(true);
      setFinalSummary(null);
      setFinalResult(null);
      setNpcFinalResult(null);
      setTrainingOutcome(undefined);
      setStartError(null);
      setFinalizeError(null);
      lastFinalizeRef.current = null;
      finalizeTargetRef.current = null;
      sessionDurationRef.current = 0;
      resetVoice();

      // Create the session on the backend for this mode, THEN record. A refused
      // start (any 4xx — PLAN_LIMIT, ORG_PLAN_LIMIT, validation — or a network
      // failure) starts nothing: no recording, so nothing is uploaded or analyzed.
      type Created = { sessionId: string; target: FinalizeTarget };
      const plan: RecordedAttemptPlan<Created> | null = trainingConfig
        ? {
            // Practising an assignment: the session is linked to it, and finalize
            // records the result from the measured scores.
            create: async () => {
              const session = await createSession(
                trainingSessionRequest(trainingConfig.trainingAssignmentId)
              ).unwrap();
              return { sessionId: session.id, target: { kind: 'training', sessionId: session.id } };
            },
            onRefused: (error) => {
              const status = (error as { status?: number })?.status;
              const limit = planLimitNotice(error);
              const unavailable = usageAuthorityUnavailableMessage(error);
              const limited = limitRefusalMessage(error);
              refuseStart(
                status === 404
                  ? 'This assignment is no longer open, so no recording was started.'
                  : limit
                  ? `${limit.message} No recording was started.`
                  : limited
                  ? `${limited} No recording was started.`
                  : unavailable
                  ? `${unavailable} No recording was started.`
                  : 'The assignment session could not be started, so no recording was started.'
              );
            },
          }
        : practiceMode === 'npc' && selectedPersonaId
        ? {
            create: async () => {
              const session = await createNpcSession({
                personaId: selectedPersonaId,
                levelId: selectedLevelId || undefined,
              }).unwrap();
              return { sessionId: session.id, target: { kind: 'npc', sessionId: session.id } };
            },
            onRefused: (error) => {
              if (refusedForPlanLimit(error, 'npc_practice')) return;
              const limited = limitRefusalMessage(error);
              if (limited) return refuseStart(`${limited} No recording was started.`);
              const unavailable = usageAuthorityUnavailableMessage(error);
              if (unavailable) return refuseStart(`${unavailable} No recording was started.`);
              console.error('Failed to create NPC session:', error);
              refuseStart('The practice call could not be started, so no recording was started. Please try again.');
            },
          }
        : practiceMode === 'ai_coach'
        ? {
            create: async () => {
              const session = await createSession({ type: AI_COACH_SESSION_TYPE }).unwrap();
              refetchAllowance();
              return { sessionId: session.id, target: { kind: 'ai_coach', sessionId: session.id } };
            },
            onRefused: (error) => {
              if (refusedForPlanLimit(error, AI_COACH_SESSION_TYPE)) return;
              const limited = limitRefusalMessage(error);
              if (limited) return refuseStart(`${limited} No recording was started.`);
              const unavailable = usageAuthorityUnavailableMessage(error);
              if (unavailable) return refuseStart(`${unavailable} No recording was started.`);
              console.error('Failed to create session:', error);
              refuseStart('The coaching session could not be started, so no recording was started. Please try again.');
            },
          }
        : null; // Free practice: no session; the analysis is metered by the voice quota.

      if (!plan) setCurrentSessionId(null);
      await beginRecordedAttempt(plan, {
        onCreated: ({ sessionId, target }) => {
          setCurrentSessionId(sessionId);
          finalizeTargetRef.current = target;
        },
        startRecording,
      });
    }
  };

  const handleStartNewSession = () => {
    finalizeTargetRef.current = null;
    lastFinalizeRef.current = null;
    setFinalizeError(null);
    setTrainingOutcome(undefined);
    setStartError(null);
    setSessionState('idle');
    setFinalSummary(null);
    setFinalResult(null);
    setNpcFinalResult(null);
    setSessionDuration(0);
    setCurrentSessionId(null);
    resetVoice();
  };

  // Get display scores from voice analysis or final result.
  //
  // R0: `null` is preserved end-to-end and means NOT MEASURED. The previous version
  // coerced every dimension with `|| 0` and substituted neighbours
  // (`pace || structure`, `confidence || delivery`), so an unmeasured vocal
  // dimension was displayed to the user as a 0% score — and a structure score could
  // silently appear under the "Pace" label.
  //
  // RC19: once an attempt is FINALIZED its recorded scores are shown (the
  // analysis numbers before it are preliminary); a free Record & Review
  // analysis has no finalize, so its analysis is what is shown.
  const displayScores = useMemo(() => {
    if (voiceScores && !npcFinalResult?.scores && !finalResult?.scores) {
      return {
        clarity: voiceScores.clarity ?? null,
        pace: voiceScores.pace ?? null,
        confidence: voiceScores.confidence ?? null,
        energy: voiceScores.energy ?? null,
        // The recording's canonical evidence overall (the server computes it with
        // the profile finalize records) — never the legacy heuristic overall.
        overall: voiceEvidence?.overall ?? null,
        filler_words: voiceAnalysis?.fillerWordCount ?? null,
      };
    }
    // A finalized attempt's overall is its canonical `attemptScore` (see
    // overallScore below); the finalize response carries no legacy overall.
    if (npcFinalResult?.scores) {
      return {
        clarity: npcFinalResult.scores.clarity ?? null,
        pace: npcFinalResult.scores.pace ?? null,
        confidence: npcFinalResult.scores.confidence ?? null,
        energy: npcFinalResult.scores.energy ?? null,
        overall: null,
        filler_words: npcFinalResult.scores.filler_words ?? null,
      };
    }
    if (finalResult?.scores) {
      return {
        clarity: finalResult.scores.clarity ?? null,
        pace: finalResult.scores.pace ?? null,
        confidence: finalResult.scores.confidence ?? null,
        energy: finalResult.scores.energy ?? null,
        overall: null,
        filler_words: finalResult.scores.filler_words ?? null,
      };
    }
    // Nothing measured yet — null, not zero.
    return {
      clarity: null,
      pace: null,
      confidence: null,
      energy: null,
      overall: null,
      filler_words: null,
    };
  }, [voiceScores, voiceEvidence, voiceAnalysis, finalResult, npcFinalResult]);

  // The Overall Score card reads the same measured value as the summary:
  // null (nothing measured yet, recording, or unscored) is "—", never 0%.
  // It used to fall back to 0 and ignore the NPC result.
  //
  // An NPC attempt's overall is the CANONICAL evidence overall the level
  // authority judged (finalize-npc data.npc.levelResult.attemptScore) — never
  // the legacy heuristic overall (voiceScores / npcFinalResult.scores), which
  // could read above the required score next to "Not yet passed". Until the
  // level result arrives (or when it is not evaluable) it is "—".
  //
  // RC19: the same for EVERY session-bound attempt (training assignment, AI
  // coach): the overall is the finalize response's `attemptScore` — the
  // canonical evidence history, analytics and the training assignment read —
  // so it agrees with the AssignmentOutcomeCard. Before finalize it is "—":
  // the analysis' overall is a preliminary legacy estimate, not the result.
  // Only a free Record & Review analysis (no session, no finalize) shows the
  // analysis overall.
  const isNpcAttempt = !trainingConfig && practiceMode === 'npc' && !!selectedPersonaId;
  const isSessionAttempt = !!trainingConfig || practiceMode === 'ai_coach';
  const overallScore: number | null = isNpcAttempt
    ? npcAttemptOverall(npcFinalResult)
    : isSessionAttempt
    ? sessionAttemptOverall(finalResult)
    : clampOrNull(displayScores.overall);
  // Analysis numbers shown before the attempt's result is recorded are preliminary.
  const scoresArePreliminary = (isNpcAttempt || isSessionAttempt) && !finalResult && !npcFinalResult;
  // Where the shown feedback came from: the model, or templated rules.
  const feedbackSource: 'model' | 'rule_based_fallback' | null =
    (finalResult?.feedback?.feedbackSource ?? null) ||
    (npcFinalResult?.feedback as { feedbackSource?: 'model' | 'rule_based_fallback' } | undefined)?.feedbackSource ||
    voiceFeedbackSource ||
    null;

  // Handle mode change
  const handleModeChange = (mode: PracticeMode) => {
    if (mode === 'ai_coach' && !canUseAiCoach) {
      return;
    }
    // RAG mode cannot be chosen while retrieval is unavailable on the server.
    if (mode === 'rag' && !ragAvailable) {
      return;
    }
    setPracticeMode(mode);
    // Clear NPC selection when switching modes
    if (mode !== 'npc') {
      setSelectedPersonaId(null);
      setSelectedLevelId(null);
    }
    // Clear content set selection when switching modes
    if (mode !== 'rag') {
      setSelectedContentSetId(null);
    }
  };

  if (isPlanLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Practice</h1>
          <p className="mt-1 text-gray-500">
            {activeTab === 'feedback'
              ? 'Review your past sessions and track improvement'
              : practiceMode === 'npc'
              ? selectedPersona
                ? `Practice call with ${selectedPersona.label}`
                : 'Pick an opponent and start a practice call'
              : practiceMode === 'ai_coach'
              ? 'Get personalized AI coaching on your delivery'
              : 'Record your practice and get AI feedback'}
          </p>
        </div>
        {sessionState === 'completed' && practiceType === 'classic' && activeTab === 'practice' && (
          <button
            onClick={handleStartNewSession}
            className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
          >
            Start New Session
          </button>
        )}
      </div>

      {trainingConfig && (
        <AssignmentContextBanner
          moduleName={practisedAssignment?.module?.name ?? null}
          minOverallScore={practisedAssignment?.module?.minOverallScore ?? null}
        />
      )}
      {startError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800" role="alert">
          {startError}
        </div>
      )}

      {/* Practice / Get Feedback Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1" aria-label="Practice tabs">
          <button
            onClick={() => setActiveTab('practice')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              activeTab === 'practice'
                ? 'border-primary-600 text-primary-700 bg-primary-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Practice
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              activeTab === 'feedback'
                ? 'border-primary-600 text-primary-700 bg-primary-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Get Feedback
          </button>
        </nav>
      </div>

      {/* ========== GET FEEDBACK TAB ========== */}
      {activeTab === 'feedback' && (
        <div className="space-y-6">
          {/* Sessions left unfinished (e.g. a crashed run): the owner can discard them. */}
          <UnfinishedPracticeSessions excludeSessionId={currentSessionId} />
          {/* Authority Level + Progress */}
          {authority && (
            <AuthorityLevelHint
              level={authority.currentLevel}
              levelLabel={authority.currentLevelLabel}
              nextLevel={authority.nextLevel}
              progress={authority.progressToNextLevel.overallProgress}
            />
          )}

          <div className="card">
            <div className="card-body text-center py-12">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                <SparklesIcon className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Coaching Feedback</h3>
              <p className="text-gray-500 max-w-md mx-auto mb-6">
                Get detailed AI analysis of your delivery, strengths, and areas for improvement.
                Start a coached practice session to receive personalized feedback.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => {
                    setActiveTab('practice');
                    handleModeChange('ai_coach');
                  }}
                  disabled={!canUseAiCoach}
                  className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Start AI Coach Session
                  {!canUseAiCoach && <span className="ml-1 text-xs opacity-75">(Pro)</span>}
                </button>
                <Link
                  to="/progress"
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  View Past Sessions
                </Link>
              </div>
            </div>
          </div>

          {/* Bridge to Test */}
          <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
            <div className="card-body flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">Practice a specific skill</h4>
                <p className="text-sm text-gray-600 mt-1">Answer questions for one skill objective. Scoring isn't available yet, so these runs are practice only.</p>
              </div>
              <Link
                to="/skills"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium whitespace-nowrap"
              >
                Skill practice
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ========== PRACTICE TAB ========== */}
      {activeTab === 'practice' && (
        <>

      {/* ========== GUIDED ENTRY (live mode, idle, guide phase) ========== */}
      {practiceType === 'live' && sessionState === 'idle' && entryPhase === 'guide' && (
        <GuidedPracticeEntry
          personas={npcPersonas}
          allPersonas={allPersonas}
          isLoading={isLoadingPersonas}
          domainFilter={npcDomainFilter}
          scenarioFilter={npcScenarioFilter}
          onDomainFilterChange={(d) => {
            setNpcDomainFilter(d);
            setNpcScenarioFilter('');
            setSelectedPersonaId(null);
          }}
          onScenarioFilterChange={(s: string) => {
            setNpcScenarioFilter(s);
            setSelectedPersonaId(null);
          }}
          onSelectPersona={(persona) => {
            setSelectedPersonaId(persona.id);
            setPracticeMode('npc');
            setEntryPhase('precall');
          }}
          onSkipToFreePlay={() => {
            setPracticeMode('free');
            setEntryPhase('session');
          }}
        />
      )}

      {/* ========== PRE-CALL SCREEN ========== */}
      {practiceType === 'live' && sessionState === 'idle' && entryPhase === 'precall' && selectedPersona && (
        <PreCallScreen
          persona={selectedPersona}
          onStart={() => {
            setEntryPhase('session');
          }}
          onBack={() => {
            setEntryPhase('guide');
            setSelectedPersonaId(null);
          }}
          onReroll={npcPersonas.length > 1 ? () => {
            const pick = pickRandomPersona(npcPersonas, [], selectedPersonaId);
            if (pick) {
              setSelectedPersonaId(pick.id);
            }
          } : undefined}
        />
      )}

      {/* ========== SESSION UI (normal flow) ========== */}
      {(entryPhase === 'session' || practiceType === 'classic') && (
      <>

      {/* Authority Level Hint */}
      {authority && sessionState === 'idle' && (
        <AuthorityLevelHint
          level={authority.currentLevel}
          levelLabel={authority.currentLevelLabel}
          nextLevel={authority.nextLevel}
          progress={authority.progressToNextLevel.overallProgress}
        />
      )}

      {/* Practice Type choice — Live Conversation (the certified two-way realtime
          runtime) or Record & Review. Offered ONLY when the server reports the
          realtime capability; otherwise Practice is Record & Review alone and
          the switcher is not rendered at all. */}
      {realtimeVoiceAvailable && sessionState === 'idle' && !trainingConfig && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-lg mx-auto">
          <button
            onClick={() => setPracticeType('live')}
            className={`flex-1 px-4 py-3 rounded-lg border-2 text-left transition-all ${
              practiceType === 'live'
                ? 'border-primary-500 bg-primary-50 shadow-sm'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <StreamIcon className={`w-4 h-4 ${practiceType === 'live' ? 'text-primary-600' : 'text-gray-400'}`} />
              <span className="text-sm font-semibold text-gray-900">Live Conversation</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Talk it through out loud. The AI listens and replies in real time.
            </p>
          </button>
          <button
            onClick={() => setPracticeType('classic')}
            className={`flex-1 px-4 py-3 rounded-lg border-2 text-left transition-all ${
              practiceType === 'classic'
                ? 'border-primary-500 bg-primary-50 shadow-sm'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <MicIcon className={`w-4 h-4 ${practiceType === 'classic' ? 'text-primary-600' : 'text-gray-400'}`} />
              <span className="text-sm font-medium text-gray-900">Record & Review</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6">Record at your own pace, get feedback after</p>
          </button>
        </div>
      )}

      {/* Voice Support Warning */}
      {!isVoiceSupported && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <WarningIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-amber-900 mb-1">Microphone Access Required</h4>
              <p className="text-sm text-amber-800">
                Your browser doesn't support audio recording, or microphone permissions were denied.
                To practice your pitch, please use a modern browser (Chrome, Firefox, Safari, or Edge) and allow microphone access when prompted.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* A recording the server refused before doing anything (a usage/safety
          limit, or usage could not be checked): its own notice, never framed as
          a recording/microphone problem. */}
      {voiceError && practiceType === 'classic' && <RecordingRefusalNotice message={voiceError} code={voiceErrorCode} />}

      {/* Voice Error Display */}
      {voiceError && practiceType === 'classic' && !isRecordingRefusalNotice(voiceErrorCode) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ErrorIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-900 mb-1">Recording Error</h4>
              <p className="text-sm text-red-800">{voiceError}</p>
              <p className="text-sm text-red-700 mt-2">
                Try refreshing the page or checking your microphone settings. If the problem persists, switch to a different browser.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ========== LIVE PRACTICE MODE UI ========== */}
      {/* The certified canonical Live Conversation runtime — the same panel
          CoachingPage mounts. Double-gated: practiceType is already forced to
          'classic' without the capability, and the panel itself refuses to run
          if the capability disappears under a loaded tab. Each Start creates a
          new canonical coaching session (realtime_audio, Development). */}
      {practiceType === 'live' && realtimeVoiceAvailable && (
        <>
          {liveStartError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800" role="alert">
              {liveStartError}
              {liveStartErrorIsCredits && <AddCreditsAction className="mt-3" />}
            </div>
          )}
          <LiveConversationPanel
            wsUrl={(import.meta.env.VITE_WS_URL as string | undefined)
              ?? `${window.location.origin.replace(/^http/, 'ws')}/ws/realtime`}
            token={accessToken}
            beginAttempt={beginLiveAttempt}
            personaName={practiceMode === 'npc' && selectedPersona ? selectedPersona.label : 'your AI coach'}
            available={realtimeVoiceAvailable}
            onEnd={(sessionId) => { void liveFinalizer.finish(sessionId); }}
          />
          {liveOutcome && (
            <SpokenAttemptOutcome
              state={liveOutcome}
              retrying={liveRetrying}
              onRetryFinalize={() => {
                setLiveRetrying(true);
                void liveFinalizer.retry(liveOutcome.sessionId).finally(() => setLiveRetrying(false));
              }}
            />
          )}
        </>
      )}

      {/* Upgrade Prompt Modal */}
      <UpgradePromptModal
        isOpen={upgradePrompt.isOpen}
        onClose={hideUpgradePrompt}
        reason={upgradePrompt.reason}
        message={upgradePrompt.message}
        action={upgradePrompt.action}
        {...(upgradePrompt.planLimit
          ? {
              errorCode: 'PLAN_LIMIT' as const,
              limitKey: upgradePrompt.planLimit.limitKey,
              planKey: upgradePrompt.planLimit.planKey,
              currentValue: upgradePrompt.planLimit.currentValue,
              maxValue: upgradePrompt.planLimit.maxValue,
            }
          : {})}
      />

      {/* ========== CLASSIC PRACTICE MODE UI ========== */}
      {practiceType === 'classic' && (
        <>
      {/* Practice Mode Selector — not for an assignment, which fixes the session */}
      {sessionState === 'idle' && !trainingConfig && (
        <div className="card">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Select Practice Mode</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Free Practice Option */}
              <button
                onClick={() => handleModeChange('free')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  practiceMode === 'free'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    practiceMode === 'free' ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    <MicIcon className={`w-5 h-5 ${
                      practiceMode === 'free' ? 'text-primary-600' : 'text-gray-500'
                    }`} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Free Practice</div>
                    <div className="text-sm text-gray-500">Record and get AI feedback</div>
                  </div>
                </div>
              </button>

              {/* AI Coach Option */}
              <button
                onClick={() => handleModeChange('ai_coach')}
                disabled={!canUseAiCoach}
                className={`p-4 rounded-lg border-2 text-left transition-all relative ${
                  practiceMode === 'ai_coach'
                    ? 'border-primary-500 bg-primary-50'
                    : canUseAiCoach
                    ? 'border-gray-200 hover:border-gray-300'
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-75'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    practiceMode === 'ai_coach' ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    <SparklesIcon className={`w-5 h-5 ${
                      practiceMode === 'ai_coach' ? 'text-primary-600' : 'text-gray-500'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      AI Voice Coach
                      {!hasCapability('ai', 'coach') && (
                        <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">Pro AI</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {hasCapability('ai', 'coach')
                        ? `Personalized AI feedback${coachingAllowanceText ? ` (AI voice allowance: ${coachingAllowanceText})` : ''}`
                        : 'Upgrade to Pro AI for access'}
                    </div>
                  </div>
                </div>

                {/* Upgrade prompt for free users */}
                {!hasCapability('ai', 'coach') && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <Link
                      to="/settings/billing"
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Upgrade to Pro AI &rarr;
                    </Link>
                  </div>
                )}

                {/* Allowance KNOWN to be used up (never shown when unknown) */}
                {hasCapability('ai', 'coach') && isKnownExhausted(coachingAllowance) && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-amber-600 mb-2">
                      You've used this month's AI voice coaching allowance. It resets at the start of next month.
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      In the meantime, try Free Practice or NPC Practice to keep improving!
                    </p>
                    <div className="flex gap-2">
                      <Link
                        to="/usage"
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View Usage
                      </Link>
                      <span className="text-xs text-gray-400">|</span>
                      <Link
                        to="/settings/billing"
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Manage Plan
                      </Link>
                    </div>
                  </div>
                )}
              </button>

              {/* NPC Practice Option */}
              <button
                onClick={() => handleModeChange('npc')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  practiceMode === 'npc'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    practiceMode === 'npc' ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    <NpcIcon className={`w-5 h-5 ${
                      practiceMode === 'npc' ? 'text-primary-600' : 'text-gray-500'
                    }`} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      NPC Practice
                      <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Game</span>
                    </div>
                    <div className="text-sm text-gray-500">Face challenging buyer personas</div>
                  </div>
                </div>
              </button>

              {/* RAG/Private Knowledge Option */}
              <button
                onClick={() => handleModeChange('rag')}
                disabled={!ragAvailable}
                aria-disabled={!ragAvailable}
                data-testid="practice-mode-rag"
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  !ragAvailable
                    ? 'border-gray-200 opacity-60 cursor-not-allowed'
                    : practiceMode === 'rag'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    practiceMode === 'rag' ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    <BookIcon className={`w-5 h-5 ${
                      practiceMode === 'rag' ? 'text-primary-600' : 'text-gray-500'
                    }`} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Private Knowledge</div>
                    <div className="text-sm text-gray-500">
                      {ragAvailable
                        ? 'Test your custom content'
                        : 'Not available: content search is not set up on this server'}
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mode Explanation Banners */}
      {sessionState === 'idle' && (
        <>
          {/* NPC Mode Explanation */}
          {practiceMode === 'npc' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <NpcIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-purple-900 mb-1">About NPC Practice</h4>
                  <p className="text-sm text-purple-800">
                    NPCs (Non-Player Characters) are AI-powered buyer personas with unique objection styles and difficulty tiers.
                    Pass each level by meeting the minimum score requirement to earn XP and unlock harder challenges.
                    Perfect your pitch against Bronze skeptics before facing Platinum tough buyers!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* RAG Mode Explanation */}
          {practiceMode === 'rag' && !ragAvailable && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" role="status" data-testid="rag-unavailable">
              <p className="text-sm text-gray-700">
                Private Knowledge practice is not available: semantic search over your content is not set up
                on this server, so your uploaded materials cannot be used in feedback.
              </p>
            </div>
          )}

          {practiceMode === 'rag' && ragAvailable && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <BookIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-blue-900 mb-1">About Private Knowledge Practice</h4>
                  <p className="text-sm text-blue-800">
                    Practice with your custom content sets. The AI will ask questions based on your uploaded materials
                    and evaluate how well you can recall and articulate the information. Great for mastering product details,
                    company history, or technical specifications.
                  </p>
                  {userContentSets.length === 0 && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-sm text-blue-700 mb-2">
                        You haven't created any content sets yet.
                      </p>
                      <Link
                        to="/content"
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Create Content Set →
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* First-time user hint */}
          {!hasSession && practiceMode === 'free' && (
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <SparklesIcon className="w-5 h-5 text-primary-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-primary-900 mb-1">Getting Started with Practice</h4>
                  <p className="text-sm text-primary-800 mb-2">
                    Record yourself delivering your pitch, elevator speech, or sales talk. You'll get instant feedback on:
                  </p>
                  <ul className="space-y-1 text-sm text-primary-800">
                    <li className="flex items-center gap-2">
                      <span className="text-primary-500">•</span>
                      <strong>Clarity:</strong> How understandable your speech is
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-500">•</span>
                      <strong>Pace:</strong> Your speaking speed (target: 120-160 WPM)
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-500">•</span>
                      <strong>Filler words:</strong> How often fillers interrupt your message
                    </li>
                  </ul>
                  <p className="text-xs text-primary-700 mt-2">
                    Vocal delivery coaching (confidence, energy, tone) is not available
                    yet — it needs acoustic analysis of your audio, which is coming in a
                    later release.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* NPC Persona Selector (when NPC mode selected and idle) */}
      {practiceMode === 'npc' && sessionState === 'idle' && (
        <NpcPersonaSelector
          personas={npcPersonas}
          levels={npcLevels}
          selectedPersonaId={selectedPersonaId}
          selectedLevelId={selectedLevelId}
          onSelectPersona={(id) => {
            setSelectedPersonaId(id);
            setSelectedLevelId(null);
          }}
          onSelectLevel={setSelectedLevelId}
          domainFilter={npcDomainFilter}
          tierFilter={npcTierFilter}
          onDomainFilterChange={setNpcDomainFilter}
          onTierFilterChange={setNpcTierFilter}
          isLoadingPersonas={isLoadingPersonas}
          isLoadingLevels={isLoadingLevels}
          levelsUnavailable={npcLevelsUnavailable}
          conversationLevelsAvailable={conversationEngineAvailable}
        />
      )}

      {/* Blueprint Selector (when idle) */}
      {sessionState === 'idle' && blueprints.length > 0 && (
        <div className="card">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Practice with a Blueprint (Optional)
            </h3>
            <select
              value={selectedBlueprintId || ''}
              onChange={(e) => {
                setSelectedBlueprintId(e.target.value || null);
                setCurrentSegmentIndex(0);
              }}
              className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">No Blueprint</option>
              {blueprints.map((bp) => (
                <option key={bp.id} value={bp.id}>
                  {bp.title} ({EVENT_TYPE_LABELS[bp.eventType]})
                </option>
              ))}
            </select>

            {/* Blueprint Segment Guide */}
            {blueprintSummary && (
              <BlueprintSegmentGuide
                summary={blueprintSummary}
                currentSegmentIndex={currentSegmentIndex}
                onSegmentChange={setCurrentSegmentIndex}
                isRecording={isRecording}
              />
            )}
          </div>
        </div>
      )}

      {/* Main practice area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Waveform and mic button */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-body">
              {/* Session status bar */}
              {isRecording && (
                <div className="flex items-center justify-between mb-4 p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-sm font-medium text-red-700">Recording</span>
                  </div>
                  <span className="text-sm font-mono text-red-600">
                    {formatDuration(sessionDuration)}
                  </span>
                </div>
              )}

              {isProcessing && (
                <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm font-medium text-blue-700">Analyzing your pitch...</span>
                  </div>
                </div>
              )}

              {sessionState === 'completed' && (
                <div className="flex items-center justify-between mb-4 p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckIcon className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">
                      {isFinalizingSession ? 'Analyzing...' : 'Session Complete'}
                    </span>
                  </div>
                  <span className="text-sm text-green-600">
                    Duration: {formatDuration(sessionDuration)}
                  </span>
                </div>
              )}

              {/* Waveform visualization */}
              <div className="h-48 mb-8">
                <Waveform isActive={isRecording} />
              </div>

              {/* Mic button */}
              <div className="flex flex-col items-center">
                <VoiceMicButton
                  isRecording={isRecording}
                  onToggle={handleToggleRecording}
                  size="large"
                  disabled={sessionState === 'completed' || isProcessing || !isVoiceSupported || (practiceMode === 'npc' && !selectedPersonaId)}
                />

                {/* Instructions */}
                <p className="text-center text-gray-500 mt-4">
                  {sessionState === 'idle' && practiceMode === 'npc' && !selectedPersonaId && 'Select a persona above to start practicing'}
                  {sessionState === 'idle' && (practiceMode !== 'npc' || selectedPersonaId) && 'Tap the microphone to start practicing'}
                  {isRecording && 'Speak clearly and confidently... Tap to stop.'}
                  {isProcessing && 'Processing your recording...'}
                  {sessionState === 'completed' && 'Great job! Review your metrics below.'}
                </p>
              </div>
            </div>
          </div>

          {/* A refused finalize, in the server's own terms (utils/attemptErrors). */}
          {finalizeError && (
            <div
              role="alert"
              data-testid="practice-finalize-error"
              data-code={finalizeError.code ?? undefined}
              className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
            >
              <p>{finalizeError.message}</p>
              {finalizeError.storedOutcome?.sessionId && (
                <Link
                  to={`/practice/sessions/${finalizeError.storedOutcome.sessionId}/review`}
                  className="mt-2 inline-block font-medium underline"
                >
                  View the stored result
                </Link>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {finalizeError.retryable && lastFinalizeRef.current && (
                  <button
                    type="button"
                    onClick={() => void retryFinalize()}
                    disabled={finalizeRetrying}
                    className="px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    {finalizeRetrying ? 'Trying again…' : 'Try again'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleStartNewSession}
                  className="px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-100"
                >
                  Start a new attempt
                </button>
              </div>
            </div>
          )}

          {/* Transcript Display */}
          {voiceTranscript && sessionState === 'completed' && (
            <div className="mt-6 card">
              <div className="card-header">
                <h3 className="font-semibold text-gray-900">Your Transcript</h3>
              </div>
              <div className="card-body">
                <p className="text-gray-700 whitespace-pre-wrap">{voiceTranscript}</p>
                {voiceAnalysis && (
                  <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-sm text-gray-500">
                    <span>{voiceAnalysis.wordCount} words</span>
                    <span>{voiceAnalysis.wordsPerMinute === null ? 'WPM not measured' : `${voiceAnalysis.wordsPerMinute} WPM`}</span>
                    {voiceAnalysis.fillerWordCount !== null && voiceAnalysis.fillerWordCount > 0 && (
                      <span className="text-amber-600">
                        {voiceAnalysis.fillerWordCount} filler word(s): {voiceAnalysis.fillerWords.join(', ')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Session Summary */}
          {finalSummary && sessionState === 'completed' && (
            <div className="mt-6 card">
              <div className="card-body">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-lg font-bold text-primary-600">
                      {overallScore === null ? '—' : Math.round(overallScore)}
                    </span>
                  </div>
                  <div className="flex-1">
                    {/* Templated tips are not coach feedback: say which this is. */}
                    <h3 className="font-semibold text-gray-900 mb-1" data-testid="feedback-heading">
                      {feedbackSource === 'rule_based_fallback' ? 'Automated tips' : 'Session Feedback'}
                    </h3>
                    {feedbackSource === 'rule_based_fallback' && (
                      <p className="text-xs text-gray-500 mb-1">
                        Generated from your scores by fixed rules, not written by an AI coach.
                      </p>
                    )}
                    <p className="text-gray-600">{finalSummary}</p>
                  </div>
                </div>

                {/* Score breakdown. For an NPC attempt these legacy dimension
                    estimates are supporting detail only: the level is decided
                    by the overall above (the canonical evidence). */}
                {scoresArePreliminary ? (
                  <p className="mt-4 pt-4 border-t text-xs text-gray-500" data-testid="practice-preliminary-detail">
                    Preliminary — estimated from your words while your attempt is being recorded. The overall appears once it is recorded.
                  </p>
                ) : isNpcAttempt ? (
                  <p className="mt-4 pt-4 border-t text-xs text-gray-500" data-testid="npc-supporting-detail">
                    Supporting detail — estimated from your words. These do not decide the level; the overall score does.
                  </p>
                ) : isSessionAttempt ? (
                  <p className="mt-4 pt-4 border-t text-xs text-gray-500" data-testid="session-supporting-detail">
                    Supporting detail — estimated from your words. Your recorded result is the overall score.
                  </p>
                ) : null}
                <div className={`${isNpcAttempt || isSessionAttempt ? 'mt-2' : 'mt-4 pt-4 border-t'} grid grid-cols-4 gap-4 text-center`}>
                  <ScoreTile
                    label="Clarity"
                    value={clampOrNull(displayScores.clarity)}
                    note="Estimated from your words, not measured from audio"
                  />
                  <ScoreTile label="Pace" value={clampOrNull(displayScores.pace)} />
                  <ScoreTile label="Confidence" value={clampOrNull(displayScores.confidence)} />
                  <ScoreTile label="Energy" value={clampOrNull(displayScores.energy)} />
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  Vocal delivery metrics (confidence, energy) are not measured yet — they
                  require acoustic analysis of your recording, which is not available in
                  this release.
                </p>

                {/* Detailed feedback from API */}
                {finalResult?.feedback && (
                  <div className="mt-4 pt-4 border-t space-y-4">
                    {/* Strengths */}
                    {finalResult.feedback.strengths.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-green-700 mb-2">Strengths</h4>
                        <ul className="space-y-1">
                          {finalResult.feedback.strengths.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Improvements */}
                    {finalResult.feedback.improvements.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-amber-700 mb-2">Areas to Improve</h4>
                        <ul className="space-y-1">
                          {finalResult.feedback.improvements.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <ArrowIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Training assignment result */}
                {trainingConfig && trainingOutcome !== undefined && (
                  <div className="mt-4 pt-4 border-t">
                    <AssignmentOutcomeCard outcome={trainingOutcome} />
                  </div>
                )}

                {/* NPC Level Result */}
                {/* The server nests the level outcome under `npc` (finalize-npc). A
                    top-level `levelResult` was read before, which the server never
                    sends, so the outcome card never appeared (staging, #225). */}
                {npcFinalResult?.npc?.levelResult && (
                  <NpcLevelResultCard result={npcFinalResult.npc.levelResult} />
                )}

                {/* Post-Session Bridge CTAs */}
                <div className="mt-6 pt-4 border-t flex flex-wrap items-center gap-3">
                  <button
                    onClick={handleStartNewSession}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    Practice Again
                  </button>
                  {/* Review: only for a session the server finalized (persisted and completed). */}
                  {currentSessionId && (finalResult || npcFinalResult) && (
                    <Link
                      to={`/practice/sessions/${currentSessionId}/review`}
                      className="px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors text-sm font-medium"
                    >
                      Review transcript
                    </Link>
                  )}
                  <button
                    onClick={() => setActiveTab('feedback')}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Get Feedback
                  </button>
                  <Link
                    to="/skills"
                    className="px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors text-sm font-medium"
                  >
                    Skill practice
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: Real-time metrics */}
        <div>
          <RealTimeMetricsCard
            metrics={metrics}
            isRecording={isRecording}
            hasSession={hasSession}
          />

          {/* Overall score card (only when active or completed) */}
          {hasSession && (
            <div className="mt-4 card">
              <div className="card-body text-center">
                <div className="text-sm text-gray-500 mb-1">Overall Score</div>
                <div
                  data-testid="practice-overall-score"
                  className={`text-4xl font-bold ${
                    overallScore === null
                      ? 'text-gray-300'
                      : overallScore >= 80
                      ? 'text-green-600'
                      : overallScore >= 60
                      ? 'text-yellow-600'
                      : 'text-orange-500'
                  }`}
                >
                  {overallScore === null ? '—' : `${Math.round(overallScore)}%`}
                </div>
                {isRecording && (
                  <div className="text-xs text-gray-400 mt-1 animate-pulse">Recording...</div>
                )}
                {isProcessing && (
                  <div className="text-xs text-blue-500 mt-1 animate-pulse">Analyzing...</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick tips */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-gray-900">Quick Tips</h2>
        </div>
        <div className="card-body">
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-primary-500">•</span>
              Speak at a moderate pace — around 120-160 words per minute
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500">•</span>
              Minimize filler words like "um", "uh", "like", "you know"
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500">•</span>
              Project confidence through clear articulation
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500">•</span>
              Use pauses strategically for emphasis
            </li>
          </ul>
        </div>
      </div>
        </>
      )}

      </>
      )}

      </>
      )}
    </div>
  );
}

// ============================================
// GUIDED PRACTICE ENTRY
// ============================================

const TIER_LABELS: Record<DifficultyTier, { label: string; icon: string; color: string }> = {
  bronze: { label: 'Easy', icon: '🥉', color: 'border-amber-300 bg-amber-50' },
  silver: { label: 'Medium', icon: '🥈', color: 'border-slate-300 bg-slate-50' },
  gold: { label: 'Hard', icon: '🥇', color: 'border-yellow-300 bg-yellow-50' },
  platinum: { label: 'Expert', icon: '💎', color: 'border-purple-300 bg-purple-50' },
  master: { label: 'Master', icon: '👑', color: 'border-red-300 bg-red-50' },
};

/**
 * Human-friendly labels for scenario keys derived from NPC personas.
 * Maps existing scenarioKey values to Topic labels.
 * If a scenarioKey is not in this map, we auto-format it.
 */
const SCENARIO_LABELS: Record<string, { label: string; description: string }> = {
  cold_call_intro: { label: 'Cold Calling', description: 'Opening calls with new prospects' },
  cold_call_real_estate: { label: 'Real Estate Calls', description: 'Cold calling property leads' },
  objection_price: { label: 'Objection Handling', description: 'Overcoming price and value objections' },
  hostile_objection: { label: 'Tough Objections', description: 'Handling aggressive pushback' },
  investor_pitch: { label: 'Investor Pitch', description: 'Presenting to investors and stakeholders' },
  basic_pitch: { label: 'Pitching', description: 'Delivering a compelling pitch' },
  negotiation_salary: { label: 'Negotiation', description: 'Salary and deal negotiations' },
  vc_deep_dive: { label: 'Deep Dive Q&A', description: 'Detailed investor due diligence' },
  board_presentation: { label: 'Board Presentation', description: 'Presenting to a board of directors' },
  media_interview: { label: 'Media Interview', description: 'Handling press and media questions' },
  crisis_communication: { label: 'Crisis Comms', description: 'Communicating under pressure' },
  networking_event: { label: 'Networking', description: 'Conversation at events and meetups' },
  storytelling_basics: { label: 'Storytelling', description: 'Crafting and delivering narratives' },
  self_introduction: { label: 'Self Introduction', description: 'Introducing yourself compellingly' },
};

function getScenarioLabel(key: string): string {
  return SCENARIO_LABELS[key]?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Focus options — UI framing labels only.
 * These influence which style tags are highlighted on persona cards
 * but do NOT filter backend results (no backend change needed).
 * Documented: Focus is UI-only for now.
 */
const FOCUS_OPTIONS = [
  { key: '', label: 'All Styles', description: 'Show all opponents' },
  { key: 'knowledge', label: 'Knowledge', description: 'Test what you know', tags: ['analytical', 'technical', 'detail-oriented'] },
  { key: 'persuasion', label: 'Persuasion', description: 'Convince and influence', tags: ['skeptical', 'resistant', 'tough'] },
  { key: 'confidence', label: 'Confidence', description: 'Handle pressure', tags: ['aggressive', 'hostile', 'confrontational'] },
  { key: 'delivery', label: 'Delivery', description: 'Improve how you communicate', tags: ['empathetic', 'conversational', 'supportive'] },
] as const;

/**
 * Pick a random persona from a list, preferring focus-matching ones.
 * Avoids returning `exclude` if the pool has more than one option.
 */
function pickRandomPersona(
  pool: PersonaWithLevelSummary[],
  focusTags: readonly string[],
  exclude?: string | null,
): PersonaWithLevelSummary | null {
  if (pool.length === 0) return null;

  // Prefer focus-matching personas if focus is active
  let candidates = pool;
  if (focusTags.length > 0) {
    const matching = pool.filter((p) => p.styleTags.some((t) => focusTags.includes(t)));
    if (matching.length > 0) candidates = matching;
  }

  // Avoid repeating the same persona if possible
  if (exclude && candidates.length > 1) {
    candidates = candidates.filter((p) => p.id !== exclude);
  }

  return candidates[Math.floor(Math.random() * candidates.length)];
}

function GuidedPracticeEntry({
  personas,
  allPersonas,
  isLoading,
  domainFilter,
  scenarioFilter,
  onDomainFilterChange,
  onScenarioFilterChange,
  onSelectPersona,
  onSkipToFreePlay,
}: {
  personas: PersonaWithLevelSummary[];
  allPersonas: PersonaWithLevelSummary[];
  isLoading: boolean;
  domainFilter: string;
  scenarioFilter: string;
  onDomainFilterChange: (domain: string) => void;
  onScenarioFilterChange: (scenario: string) => void;
  onSelectPersona: (persona: PersonaWithLevelSummary) => void;
  onSkipToFreePlay: () => void;
}) {
  const [focusKey, setFocusKey] = useState('');

  // ---- STEP 1: Industry (domain) ----
  const domains = useMemo(() => {
    const source = allPersonas.length > 0 ? allPersonas : personas;
    const unique = [...new Set(source.map((p) => p.domainKey))];
    return unique.map((key) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      count: source.filter((p) => p.domainKey === key).length,
    }));
  }, [allPersonas, personas]);

  // ---- STEP 2: Topic (scenarioKey) ----
  // Derive unique scenario keys from personas in the selected domain
  const topics = useMemo(() => {
    const source = allPersonas.length > 0 ? allPersonas : personas;
    const domainPersonas = domainFilter
      ? source.filter((p) => p.domainKey === domainFilter)
      : source;
    const scenarioKeys = domainPersonas
      .map((p) => p.scenarioKey)
      .filter((k): k is string => !!k);
    const unique = [...new Set(scenarioKeys)];
    return unique.map((key) => ({
      key,
      label: getScenarioLabel(key),
      description: SCENARIO_LABELS[key]?.description ?? '',
      count: domainPersonas.filter((p) => p.scenarioKey === key).length,
    }));
  }, [allPersonas, personas, domainFilter]);

  // ---- STEP 3: Focus (UI-only style emphasis) ----
  const selectedFocus = FOCUS_OPTIONS.find((f) => f.key === focusKey);
  const focusTags = selectedFocus && 'tags' in selectedFocus ? selectedFocus.tags : [];

  // ---- STEP 4: Opponents ----
  // Apply focus as a soft sort (matching tags sort to top) not a hard filter
  const tierOrder: DifficultyTier[] = ['bronze', 'silver', 'gold', 'platinum', 'master'];
  const sortedPersonas = useMemo(() => {
    const list = [...personas];
    list.sort((a, b) => {
      // If focus is active, prioritize personas whose styleTags overlap
      if (focusTags.length > 0) {
        const aMatch = a.styleTags.some((t) => focusTags.includes(t as never));
        const bMatch = b.styleTags.some((t) => focusTags.includes(t as never));
        if (aMatch && !bMatch) return -1;
        if (!aMatch && bMatch) return 1;
      }
      return tierOrder.indexOf(a.difficultyTier) - tierOrder.indexOf(b.difficultyTier);
    });
    return list;
  }, [personas, focusTags]);

  const hasTopics = topics.length > 0;
  const hasDomain = domainFilter !== '';

  const handleRandomCall = () => {
    const pick = pickRandomPersona(personas, focusTags);
    if (pick) onSelectPersona(pick);
  };

  return (
    <div className="space-y-6">
      {/* Random Call quick-start */}
      {!isLoading && personas.length > 0 && (
        <button
          onClick={handleRandomCall}
          className="w-full card border-2 border-dashed border-primary-300 hover:border-primary-500 hover:shadow-md transition-all group"
        >
          <div className="card-body flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                <span className="text-2xl">🎲</span>
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Random Call</h3>
                <p className="text-sm text-gray-500">
                  Jump straight into a practice call with a surprise opponent
                </p>
              </div>
            </div>
            <span className="text-primary-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
              Go →
            </span>
          </div>
        </button>
      )}

      {/* Step 1 — Industry */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">1</span>
            <h3 className="font-semibold text-gray-900">Industry</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4 ml-8">What field do you work in?</p>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 ml-8">
              <button
                onClick={() => onDomainFilterChange('')}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  !hasDomain
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                All Industries
              </button>
              {domains.map((d) => (
                <button
                  key={d.key}
                  onClick={() => onDomainFilterChange(d.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    domainFilter === d.key
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Step 2 — Topic (derived from scenarioKey) */}
      {!isLoading && hasTopics && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">2</span>
              <h3 className="font-semibold text-gray-900">Topic</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4 ml-8">What are you working on?</p>

            <div className="flex flex-wrap gap-2 ml-8">
              <button
                onClick={() => onScenarioFilterChange('')}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  scenarioFilter === ''
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                All Topics
              </button>
              {topics.map((t) => (
                <button
                  key={t.key}
                  onClick={() => onScenarioFilterChange(t.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    scenarioFilter === t.key
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  title={t.description}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 3 — Focus (UI-only) */}
      {!isLoading && sortedPersonas.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">{hasTopics ? '3' : '2'}</span>
              <h3 className="font-semibold text-gray-900">Focus</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4 ml-8">What do you want to improve?</p>

            <div className="flex flex-wrap gap-2 ml-8">
              {FOCUS_OPTIONS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFocusKey(f.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    focusKey === f.key
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  title={f.description}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 4 — Pick Opponent */}
      {!isLoading && sortedPersonas.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">{hasTopics ? '4' : '3'}</span>
              <h3 className="font-semibold text-gray-900">Pick Your Opponent</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4 ml-8">Choose who you want to practice against</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ml-8">
              {sortedPersonas.map((persona) => {
                const tier = TIER_LABELS[persona.difficultyTier];
                const progressPercent = persona.totalLevels > 0
                  ? Math.round((persona.passedLevels / persona.totalLevels) * 100)
                  : 0;
                const matchesFocus = focusTags.length > 0 && persona.styleTags.some((t) => focusTags.includes(t as never));

                return (
                  <button
                    key={persona.id}
                    onClick={() => onSelectPersona(persona)}
                    className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md hover:border-primary-400 ${
                      matchesFocus ? 'ring-2 ring-primary-200 ' + tier.color : tier.color
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">{tier.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{persona.label}</div>
                        <div className="text-xs text-gray-500 capitalize mt-0.5">
                          {tier.label} difficulty
                          {matchesFocus && (
                            <span className="ml-1.5 text-primary-600 font-medium">· Recommended</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{persona.description}</p>
                        {persona.totalLevels > 0 && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-gray-400 mb-0.5">
                              <span>{persona.passedLevels}/{persona.totalLevels} levels</span>
                              <span>{progressPercent}%</span>
                            </div>
                            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary-500 rounded-full"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* No opponents found */}
      {!isLoading && sortedPersonas.length === 0 && (domainFilter || scenarioFilter) && (
        <div className="card">
          <div className="card-body text-center py-8">
            <p className="text-gray-500">No opponents found for this combination.</p>
            <button
              onClick={() => { onDomainFilterChange(''); onScenarioFilterChange(''); }}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* Quick start — skip guided entry */}
      <div className="text-center">
        <button
          onClick={onSkipToFreePlay}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          or skip and start free practice →
        </button>
      </div>
    </div>
  );
}

// ============================================
// PRE-CALL SCREEN
// ============================================

function PreCallScreen({
  persona,
  onStart,
  onBack,
  onReroll,
}: {
  persona: PersonaWithLevelSummary;
  onStart: () => void;
  onBack: () => void;
  onReroll?: () => void;
}) {
  const tier = TIER_LABELS[persona.difficultyTier];
  const [phase, setPhase] = useState<'ready' | 'calling'>('ready');

  const handleCall = () => {
    setPhase('calling');
    // Brief delay to show the calling animation, then start session
    setTimeout(() => {
      onStart();
    }, 1500);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
      {/* Persona avatar */}
      <div className={`relative ${phase === 'calling' ? 'animate-pulse' : ''}`}>
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-lg">
          {persona.avatarUrl ? (
            <img src={persona.avatarUrl} alt={persona.label} className="w-24 h-24 rounded-full object-cover" />
          ) : (
            <span className="text-4xl">{tier.icon}</span>
          )}
        </div>
        {phase === 'calling' && (
          <div className="absolute inset-0 rounded-full border-4 border-primary-400 animate-ping" />
        )}
      </div>

      {/* Info */}
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-900">{persona.label}</h2>
        <p className="text-sm text-gray-500 mt-1 capitalize">{tier.label} difficulty</p>
        {persona.styleTags.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 mt-2">
            {persona.styleTags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status */}
      {phase === 'calling' ? (
        <div className="text-center">
          <p className="text-lg font-medium text-primary-600 animate-pulse">Connecting...</p>
          <p className="text-sm text-gray-500 mt-1">Preparing your voice session</p>
        </div>
      ) : (
        <p className="text-sm text-gray-500 max-w-xs text-center">
          {persona.description}
        </p>
      )}

      {/* Actions */}
      {phase === 'ready' && (
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Back
          </button>
          {onReroll && (
            <button
              onClick={onReroll}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors flex items-center gap-1.5"
            >
              <span>🎲</span>
              Different Opponent
            </button>
          )}
          <button
            onClick={handleCall}
            className="px-8 py-3 bg-green-600 text-white rounded-full font-semibold hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <PhoneIcon className="w-5 h-5" />
            Start Call
          </button>
        </div>
      )}
    </div>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

// Authority level colors
const LEVEL_COLORS: Record<AuthorityLevelKey, { bg: string; text: string; gradient: string }> = {
  novice: { bg: 'bg-gray-100', text: 'text-gray-700', gradient: 'from-gray-400 to-gray-500' },
  bronze: { bg: 'bg-amber-100', text: 'text-amber-700', gradient: 'from-amber-500 to-amber-600' },
  silver: { bg: 'bg-slate-200', text: 'text-slate-700', gradient: 'from-slate-400 to-slate-500' },
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700', gradient: 'from-yellow-400 to-yellow-500' },
  platinum: { bg: 'bg-purple-100', text: 'text-purple-700', gradient: 'from-purple-500 to-indigo-600' },
};

function AuthorityLevelHint({
  level,
  levelLabel,
  nextLevel,
  progress,
}: {
  level: AuthorityLevelKey;
  levelLabel: string;
  nextLevel: { label: string; minSessions: number; minOverallScore: number } | null;
  progress: number;
}) {
  const colors = LEVEL_COLORS[level];

  return (
    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${colors.gradient}`}>
          <TrophyIcon className="w-4 h-4 text-white" />
        </div>
        <div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
            {levelLabel}
          </span>
          {nextLevel && (
            <span className="ml-2 text-xs text-gray-500">
              {Math.round(progress)}% to {nextLevel.label}
            </span>
          )}
        </div>
      </div>
      {nextLevel && (
        <div className="text-xs text-gray-500 text-right hidden sm:block">
          <span>Need: {nextLevel.minSessions} sessions, {nextLevel.minOverallScore}+ avg score</span>
        </div>
      )}
    </div>
  );
}

function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function StreamIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
    </svg>
  );
}

// ============================================
// Blueprint Segment Guide Component
// ============================================

interface BlueprintSegmentGuideProps {
  summary: BlueprintSummary;
  currentSegmentIndex: number;
  onSegmentChange: (index: number) => void;
  isRecording: boolean;
}

/**
 * One score tile in the results grid.
 *
 * `null` means the dimension was not measured, and renders as an explicit
 * "Not measured" — never as "0%", which users read as a failing score.
 */
function ScoreTile({ label, value, note }: { label: string; value: number | null; note?: string }) {
  return (
    <div>
      {value === null ? (
        <div className="text-sm font-medium text-gray-400 italic py-1.5">Not measured</div>
      ) : (
        <div className="text-2xl font-bold text-gray-900">{Math.round(value)}%</div>
      )}
      <div className="text-xs text-gray-500">{label}</div>
      {note && value !== null && <div className="text-[10px] text-gray-400">{note}</div>}
    </div>
  );
}

function BlueprintSegmentGuide({
  summary,
  currentSegmentIndex,
  onSegmentChange,
  isRecording,
}: BlueprintSegmentGuideProps) {
  const segments = [...summary.segments].sort((a, b) => a.orderIndex - b.orderIndex);
  const currentSegment = segments[currentSegmentIndex];

  if (segments.length === 0) {
    return (
      <div className="mt-4 p-4 bg-amber-50 rounded-lg">
        <p className="text-sm text-amber-700">
          This blueprint has no segments yet.{' '}
          <Link to={`/blueprints/${summary.id}`} className="underline hover:text-amber-900">
            Add some segments
          </Link>{' '}
          to create your outline.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      {/* Segment Progress Bar */}
      <div className="flex items-center gap-1">
        {segments.map((seg, idx) => (
          <button
            key={seg.orderIndex}
            onClick={() => onSegmentChange(idx)}
            disabled={isRecording}
            className={`flex-1 h-2 rounded-full transition-all ${
              idx === currentSegmentIndex
                ? 'bg-primary-500'
                : idx < currentSegmentIndex
                ? 'bg-green-400'
                : 'bg-gray-200'
            } ${!isRecording ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
            title={BP_FLOWER_LABELS[seg.flowerKey]}
          />
        ))}
      </div>

      {/* Current Segment Card */}
      <div className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center text-xs font-medium bg-primary-500 text-white rounded-full">
              {currentSegmentIndex + 1}
            </span>
            <span className="font-medium text-gray-900">
              {BP_FLOWER_LABELS[currentSegment.flowerKey]}
            </span>
            {currentSegment.bouquetKey && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                {BOUQUET_LABELS[currentSegment.bouquetKey]}
              </span>
            )}
          </div>

          {/* Stage Position */}
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <span>{STAGE_POSITION_ICONS[currentSegment.stagePosition]}</span>
            <span>{STAGE_POSITION_LABELS[currentSegment.stagePosition]}</span>
          </div>
        </div>

        {/* Petals for this segment */}
        {currentSegment.petalTitles.length > 0 ? (
          <div>
            <p className="text-xs text-gray-500 mb-2">
              Talking Points ({currentSegment.petalTitles.length}):
            </p>
            <ul className="space-y-1">
              {currentSegment.petalTitles.map((title, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="text-primary-400">•</span>
                  {title}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">
            No talking points for this segment.
          </p>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-primary-100">
          <button
            onClick={() => onSegmentChange(Math.max(0, currentSegmentIndex - 1))}
            disabled={currentSegmentIndex === 0 || isRecording}
            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <span className="text-xs text-gray-400">
            {currentSegmentIndex + 1} of {segments.length}
          </span>

          <button
            onClick={() => onSegmentChange(Math.min(segments.length - 1, currentSegmentIndex + 1))}
            disabled={currentSegmentIndex === segments.length - 1 || isRecording}
            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Next
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Stage Movement Hint */}
      {currentSegmentIndex > 0 && (
        <StageMovementHint
          fromPosition={segments[currentSegmentIndex - 1].stagePosition}
          toPosition={currentSegment.stagePosition}
        />
      )}
    </div>
  );
}

// Stage Movement Hint Component
function StageMovementHint({
  fromPosition,
  toPosition,
}: {
  fromPosition: string;
  toPosition: string;
}) {
  if (fromPosition === toPosition) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-700">
      <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>
        Move from <strong>{STAGE_POSITION_LABELS[fromPosition as keyof typeof STAGE_POSITION_LABELS]}</strong>
        {' → '}
        <strong>{STAGE_POSITION_LABELS[toPosition as keyof typeof STAGE_POSITION_LABELS]}</strong>
      </span>
    </div>
  );
}

// ============================================
// NPC Practice Components
// ============================================

function NpcIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

const TIER_COLORS: Record<DifficultyTier, { bg: string; text: string; badge: string }> = {
  bronze: { bg: 'bg-amber-100', text: 'text-amber-700', badge: 'bg-amber-500' },
  silver: { bg: 'bg-slate-100', text: 'text-slate-700', badge: 'bg-slate-400' },
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700', badge: 'bg-yellow-500' },
  platinum: { bg: 'bg-purple-100', text: 'text-purple-700', badge: 'bg-purple-500' },
  master: { bg: 'bg-red-100', text: 'text-red-700', badge: 'bg-red-600' },
};

const TIER_ICONS: Record<DifficultyTier, string> = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  platinum: '💎',
  master: '👑',
};

interface NpcPersonaSelectorProps {
  personas: PersonaWithLevelSummary[];
  levels: LevelWithProgress[];
  selectedPersonaId: string | null;
  selectedLevelId: string | null;
  onSelectPersona: (id: string | null) => void;
  onSelectLevel: (id: string | null) => void;
  domainFilter: string;
  tierFilter: DifficultyTier | '';
  onDomainFilterChange: (value: string) => void;
  onTierFilterChange: (value: DifficultyTier | '') => void;
  isLoadingPersonas: boolean;
  isLoadingLevels: boolean;
  /** The levels request failed or answered something that is not a list. */
  levelsUnavailable?: boolean;
  /**
   * The server's conversation engine is on (capabilities.conversation.engine).
   * A conversation-based level attempt needs it; Record & Review levels do not.
   */
  conversationLevelsAvailable?: boolean;
}

/** Engine off: a level is attempted with Record & Review, not in a conversation. */
export const LEVEL_CONVERSATION_RECORD_INSTEAD_NOTE =
  'Level attempts in a conversation are not available right now. Record your attempt below with Record & Review — it counts toward this level.';

function NpcPersonaSelector({
  personas,
  levels,
  selectedPersonaId,
  selectedLevelId,
  onSelectPersona,
  onSelectLevel,
  domainFilter,
  tierFilter,
  onDomainFilterChange,
  onTierFilterChange,
  isLoadingPersonas,
  isLoadingLevels,
  levelsUnavailable = false,
  conversationLevelsAvailable = false,
}: NpcPersonaSelectorProps) {
  const selectedPersona = personas.find((p) => p.id === selectedPersonaId);
  const selectedLevel = levels.find((l) => l.id === selectedLevelId);

  // Get unique domains from personas
  const domains = [...new Set(personas.map((p) => p.domainKey))];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Domain</label>
              <select
                value={domainFilter}
                onChange={(e) => onDomainFilterChange(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Domains</option>
                {domains.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain.charAt(0).toUpperCase() + domain.slice(1).replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Difficulty</label>
              <select
                value={tierFilter}
                onChange={(e) => onTierFilterChange(e.target.value as DifficultyTier | '')}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Tiers</option>
                <option value="bronze">🥉 Bronze</option>
                <option value="silver">🥈 Silver</option>
                <option value="gold">🥇 Gold</option>
                <option value="platinum">💎 Platinum</option>
                <option value="master">👑 Master</option>
              </select>
            </div>
            <Link
              to="/npc/progress"
              className="ml-auto text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View All Progress →
            </Link>
          </div>
        </div>
      </div>

      {/* Persona Grid */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold text-gray-900">Select an Opponent</h3>
        </div>
        <div className="card-body">
          {isLoadingPersonas ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
            </div>
          ) : personas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No personas found. Try adjusting your filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {personas.map((persona) => (
                <NpcPersonaCard
                  key={persona.id}
                  persona={persona}
                  isSelected={persona.id === selectedPersonaId}
                  onSelect={() => onSelectPersona(persona.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Level Selector (when persona selected) */}
      {selectedPersona && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              Select Level for {selectedPersona.label}
            </h3>
            <button
              onClick={() => onSelectPersona(null)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change Opponent
            </button>
          </div>
          <div className="card-body">
            {isLoadingLevels ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
              </div>
            ) : levelsUnavailable ? (
              <div className="text-center py-6 text-gray-500" data-testid="npc-levels-unavailable">
                Levels could not be loaded for this persona. You can still practise with it.
              </div>
            ) : levels.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                No levels available for this persona yet.
              </div>
            ) : (
              <div className="space-y-3">
                {levels.map((level) => (
                  <NpcLevelCard
                    key={level.id}
                    level={level}
                    isSelected={level.id === selectedLevelId}
                    onSelect={() => !level.isLocked && onSelectLevel(level.id)}
                  />
                ))}
              </div>
            )}
            {/* Start level: a canonical conversation bound to the selected level
                (Written Chat, or Live Conversation when realtime is on). Only an
                unlocked level can be selected, so a locked one never offers it. */}
            {/* Without the engine a "conversation" would be the legacy runtime,
                which binds no level and scores nothing — so no Start level is
                offered; the Record & Review attempt below still counts. */}
            {selectedLevel && !selectedLevel.isLocked && !conversationLevelsAvailable && (
              <p className="mt-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700" data-testid="level-conversation-unavailable">
                {LEVEL_CONVERSATION_RECORD_INSTEAD_NOTE}
              </p>
            )}
            {selectedLevel && !selectedLevel.isLocked && conversationLevelsAvailable && (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-primary-50 p-3">
                <p className="text-sm text-gray-700">
                  Attempt <span className="font-medium">{selectedLevel.displayName}</span> in a scored conversation
                  (pass at {selectedLevel.minOverallScore}).
                </p>
                <Link
                  to={levelConversationUrl(selectedPersona.key, selectedLevel.id)}
                  className="btn-primary px-4 py-2 text-sm whitespace-nowrap"
                >
                  Start level
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NpcPersonaCard({
  persona,
  isSelected,
  onSelect,
}: {
  persona: PersonaWithLevelSummary;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const tierColors = TIER_COLORS[persona.difficultyTier];
  const tierIcon = TIER_ICONS[persona.difficultyTier];
  const progressPercent = persona.totalLevels > 0
    ? Math.round((persona.passedLevels / persona.totalLevels) * 100)
    : 0;

  return (
    <button
      onClick={onSelect}
      className={`p-4 rounded-lg border-2 text-left transition-all ${
        isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${tierColors.bg}`}>
          {persona.avatarUrl ? (
            <img src={persona.avatarUrl} alt={persona.label} className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <span className="text-2xl">{tierIcon}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Name and Tier */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 truncate">{persona.label}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${tierColors.bg} ${tierColors.text} capitalize`}>
              {persona.difficultyTier}
            </span>
          </div>

          {/* Description */}
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{persona.description}</p>

          {/* Progress */}
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{persona.passedLevels} / {persona.totalLevels} levels</span>
              <span>{progressPercent}%</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${tierColors.badge}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Style Tags */}
          {persona.styleTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {persona.styleTags.slice(0, 3).map((tag) => (
                <span key={tag} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function NpcLevelCard({
  level,
  isSelected,
  onSelect,
}: {
  level: LevelWithProgress;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const isPassed = level.progress?.passed;

  return (
    <button
      onClick={onSelect}
      disabled={level.isLocked}
      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
        level.isLocked
          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
          : isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-4">
        {/* Level Number */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
          isPassed
            ? 'bg-green-100 text-green-700'
            : level.isLocked
            ? 'bg-gray-200 text-gray-400'
            : 'bg-primary-100 text-primary-700'
        }`}>
          {level.isLocked ? (
            <LockIcon className="w-4 h-4" />
          ) : isPassed ? (
            <CheckIcon className="w-5 h-5" />
          ) : (
            level.levelNumber
          )}
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{level.displayName}</span>
            {isPassed && (
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                Passed
              </span>
            )}
          </div>
          {level.description && (
            <p className="text-sm text-gray-500 mt-0.5">{level.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
            <span>Min Score: {level.minOverallScore}%</span>
            <span>+{level.rewardXp} XP</span>
            {level.progress && (
              <span>
                Best:{' '}
                {level.progress.bestOverallScore === null
                  ? 'not measured'
                  : `${Math.round(level.progress.bestOverallScore)}%`}
              </span>
            )}
          </div>
        </div>

        {/* Attempts */}
        {level.progress && level.progress.attempts > 0 && (
          <div className="text-right text-sm">
            <div className="text-gray-500">{level.progress.attempts} attempts</div>
          </div>
        )}
      </div>
    </button>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

